import { t } from 'i18next'
import { z } from 'zod'

// ビュー(シートやKPIなど)に対して有効な権限アクションン
export const viewPolicyActionSchema = z.enum([
  'view-read', // ビュー(全般)の閲覧権限
  'view-display-config-edit', // ビューの表示設定編集
  'sheet-update', // シートの設定更新
  'sheet-delete',
  'sheet-create-new-record', // レコードの新規作成
  'sheet-save-record', // レコードの保存(=更新,新規作成,削除すべて)
  'sheet-delete-record', // レコードの削除
  'kpi-update',
  'kpi-delete',
  'kpi-create-new-record',
  'kpi-save-record',
  'kpi-delete-record',
  'kpi-pivot-update',
  'kpi-pivot-delete',
])

export type ViewPolicyAction = z.infer<typeof viewPolicyActionSchema>

// SALESCOREカスタムオブジェクトに対して有効な権限アクション
export const salescoreObjectPolicyActionSchema = z.enum([
  'salescore-object-edit-record',
  'salescore-object-delete-record',
  'salescore-object-create-record',
])

export type SalescoreObjectPolicyAction = z.infer<typeof salescoreObjectPolicyActionSchema>

// ユーザー(ロール)に対して有効な権限アクション
export const policyActionSchema = z.enum([
  // 全般
  'manage-policy',
  // シート関連
  // "sheet-read", // 閲覧系はいったんなしという話になった。
  'sheet-create',
  'sheet-open-relation-input-form', // 参照先レコードの新規作成フォームを開ける
  'sheet-download-csv',
  'convert-lead',
  // 「権限設定の権限」は、実装の都合上と、権限の粒度に迷っているのもあり、一旦manage-policyとしてまとめた。
  // 一旦は個別で設定することはないのでこれでいいが、必要になり次第統廃合する予定
  // "sheet-manage-policy",
  // KPI関連
  // "kpi-read",
  'kpi-create',
  'kpi-open-relation-input-form',
  'kpi-download-csv',
  // "kpi-manage-policy",
  // ダッシュボード関連
  // "kpi-pivot-read",
  'kpi-pivot-create',
  'kpi-pivot-download-csv',
  // "kpi-pivot-manage-policy",
  // ビュー全般
  // eslint-disable-next-line sonarjs/todo-tag
  // TODO: 並び替えは権限で制御するかどうか検討中
  // ビューグループ関連
  // "view-group-read",
  'view-group-create',
  // "view-group-update", // 現状要件にないので外している
  'view-group-delete',
  // その他
  'manage-organization-setting',
  // 連携先設定 (/settings/sources) の閲覧・操作。UI ラベルは「連携先設定」。
  // #9164 でオブジェクト一覧側の権限を manage-elt-model へ切り出し、本 action のスコープは連携先設定のみになった。
  'manage-connection',
  // オブジェクト一覧 (/settings/elt_models) の閲覧・設定。
  // シートのカスタムカラムの追加は本 action の対象外で、customColumn ライセンスとシートの更新権限で制御する
  'manage-elt-model',
  // オブジェクトごとのレコード既定公開範囲 (OWD) の設定 (E0 / #9659)。
  // 閲覧側の可視性判定 (isOwdExempt) とは独立で、この action は書き込み口の認可のみを担う
  'manage-record-visibility',
  'manage-goal',
  'manage-dimension-group',
  'manage-users',
  'manage-user-groups',
  'manage-group-assignment-history',
  'manage-invitation',
  'read-user-activity',

  // スナップショットオブジェクト一覧画面
  'snapshot-object-read',
  // 'snapshot-object-mange', // この予定だがまだ作らない
  'snapshot-object-schedule-manage', // 現状は削除期限のみ可能だが、将来的には取得期限も包括するか検討中

  // 現状adminのみ
  // Reverse ELT 機能は #6965 で廃止済み。この action を参照する ability 判定は無い。
  // 撤去は #9861 で行い、そのとき policyActionLabelJaMap のラベルと
  // managerRolePolicyDefinition の既定値も併せて削除する
  'manage-reverse-elt',
  'manage-internal', // 内部情報の取得など
  ...viewPolicyActionSchema.options,
])

export type PolicyAction = z.infer<typeof policyActionSchema>

// こういう系をi18n的にやるか、tsのコードでやるか迷い中
// tsでやってしまった方が型が効くので、漏れが少ない
// i18n:
export const policyActionLabelJaMap = (): Record<PolicyAction | SalescoreObjectPolicyAction, string> => ({
  'manage-policy': t('権限設定の操作'), // ユーザーがカスタマイズ仕様はしばらく後にする予定。
  'view-read': t('ビューの閲覧'),
  'view-display-config-edit': t('ビューの閲覧制限解除'),
  // ['sheet-read']: "シートの閲覧",
  'sheet-create': t('シート作成'),
  'sheet-update': t('シート編集'),
  'sheet-delete': t('シートの削除'),
  'sheet-create-new-record': t('シートでレコードの新規作成'),
  'sheet-save-record': t('シートでレコードの保存'),
  'sheet-delete-record': t('シートでレコードの削除'),
  'sheet-open-relation-input-form': t('シートの参照先セルで新規作成モーダルを開く'),
  'sheet-download-csv': t('シートのCSVダウンロード'),
  'convert-lead': t('リードの昇格'),
  // ['sheet-manage-policy']: "シートの権限設定",
  // ['kpi-read']: "KPIの閲覧",
  'kpi-create': t('KPI作成'),
  'kpi-update': t('KPI編集'),
  'kpi-delete': t('KPIの削除'),
  'kpi-create-new-record': t('KPIシートでレコードの新規作成'),
  'kpi-save-record': t('KPIシートでレコードの保存'),
  'kpi-delete-record': t('KPIシートでレコードの削除'),
  'kpi-open-relation-input-form': t('KPIシートの参照先セルで新規作成モーダルを開く'),
  'kpi-download-csv': t('KPIのCSVダウンロード'),
  // ['kpi-manage-policy']: "KPIの権限設定",
  // ['kpi-pivot-read']: "ダッシュボードの閲覧",
  'kpi-pivot-create': t('ダッシュボード作成'),
  'kpi-pivot-update': t('ダッシュボード編集'),
  'kpi-pivot-delete': t('ダッシュボードの削除'),
  'kpi-pivot-download-csv': t('ダッシュボードのCSVダウンロード'),
  // ['kpi-pivot-manage-policy']: "ダッシュボードの権限設定",
  // ['view-group-read']: "フォルダの閲覧",
  'view-group-create': t('フォルダの作成'),
  // ['view-group-update']: "フォルダの編集",
  'view-group-delete': t('フォルダの削除'),
  // その他
  'manage-organization-setting': t('ユーザーグループ設定の操作'),
  'manage-connection': t('連携先設定の操作'),
  'manage-elt-model': t('オブジェクト設定の操作'),
  'manage-record-visibility': t('レコード公開範囲の設定'),
  'manage-goal': t('目標の操作'),
  'manage-dimension-group': t('軸グループの操作'),
  'manage-users': t('ユーザーの操作'),
  'manage-user-groups': t('ユーザーグループの操作'),
  'manage-group-assignment-history': t('所属履歴の操作'),
  'manage-invitation': t('招待の操作'),
  'read-user-activity': t('利用状況を閲覧'),
  'snapshot-object-read': t('スナップショットオブジェクトの閲覧'),
  'snapshot-object-schedule-manage': t('スナップショット保持期間の操作'),
  // admin
  'manage-reverse-elt': t('R-ELTの設定'),
  'manage-internal': t('内部情報の取得'),
  'salescore-object-edit-record': t('レコードの編集'),
  'salescore-object-delete-record': t('レコードの削除'),
  'salescore-object-create-record': t('レコードの新規作成'),
})
